/**
 * Parser für FlashLive throw-by-throw-Daten (Darts).
 *
 * Semantik der BLOCKS (live verifiziert am 2026-07-12, European Tour 10 Finale):
 * - Ein Block ≈ ein Leg. SCORE_HISTORY enthält die Aufnahmen (Restpunkte nach
 *   jedem Wurf), der Eintrag mit IS_CHANGED=1 markiert den Werfer.
 * - Die SCORE_HISTORY endet NIEMALS bei 0 — der Checkout-Wurf wird nicht
 *   angehängt. Der Checkout ist der letzte Rest des Leg-Gewinners (≤ 170).
 * - Der LETZTE Block eines laufenden Matches ist ein HYBRID: SCORE_HISTORY
 *   enthält die Würfe des LAUFENDEN Legs, aber MAIN_SCORE/IS_CHANGED und
 *   SERVICE_LOST_PARTICIPANT_TYPE beschreiben noch das VORHERIGE Leg.
 *   → Ein Leg gilt NUR als beendet, wenn ein nachfolgender Block existiert
 *   oder das Match beendet ist. LOST/IS_CHANGED des letzten Blocks eines
 *   laufenden Matches sind KEINE Sieg-Signale für das laufende Leg.
 * - MAIN_SCORE eines eingefrorenen Blocks = Match-Stand NACH diesem Leg.
 *   Gewinner daher robust über das Delta zum Vorgänger-Block ableiten.
 * - TYPE:"TEXT_SPEC"-Einträge sind Highlight-Marker (z.B. "140", "180") und
 *   beziehen sich auf die direkt davor gelistete Aufnahme.
 * - BLOCKS kann im Leg-Übergang (und sporadisch mitten im Leg) 30–90s komplett
 *   leer sein — Aufrufer müssen dann den letzten Stand behalten.
 */

export interface ScoreboardVisit {
  /** 1 = Heim, 2 = Auswärts */
  team: 1 | 2;
  /** Geworfene Punkte dieser Aufnahme (null, wenn nicht ableitbar) */
  thrown: number | null;
  /** Restpunkte nach der Aufnahme */
  remaining: number;
  /** Highlight-Marker der Aufnahme (z.B. "140", "180") */
  highlight: string | null;
}

export interface ScoreboardLeg {
  /** 1-basierte Leg-Nummer im Match */
  legNumber: number;
  isFinished: boolean;
  /** 1 = Heim, 2 = Auswärts, null = läuft noch / unbekannt */
  winner: 1 | 2 | null;
  /** Checkout-Wert des Gewinners (letzter Rest ≤ 170), null wenn unbekannt */
  checkout: number | null;
  /** Anwerfer des Legs: 1 | 2 | null */
  starter: 1 | 2 | null;
  visits: ScoreboardVisit[];
  /** Letzter bekannter Rest je Spieler (laufendes Leg: aktueller Spielstand) */
  homeRemaining: number;
  awayRemaining: number;
  /** Match-Stand (Legs) nach diesem Leg — nur bei fertigen Legs gesetzt */
  matchScore: { home: number; away: number } | null;
}

export interface ParsedScoreboard {
  legs: ScoreboardLeg[];
  /** Das laufende Leg (letztes, nicht beendetes) — null wenn keins läuft */
  currentLeg: ScoreboardLeg | null;
}

const toInt = (v: any): number | null => {
  const n = parseInt(String(v ?? ''), 10);
  return isNaN(n) ? null : n;
};

const parseMainScore = (block: any): { home: number; away: number; changedTeam: 1 | 2 | null } => {
  let home = 0;
  let away = 0;
  let changedTeam: 1 | 2 | null = null;
  const mainScore = Array.isArray(block?.MAIN_SCORE) ? block.MAIN_SCORE : [];
  for (const ms of mainScore) {
    const order = toInt(ms?.TEAM_ORDER);
    const value = toInt(ms?.VALUE) ?? 0;
    if (order === 1) home = value;
    if (order === 2) away = value;
    if (toInt(ms?.IS_CHANGED) === 1 && (order === 1 || order === 2)) {
      changedTeam = order as 1 | 2;
    }
  }
  return { home, away, changedTeam };
};

export function parseThrowByThrow(blocks: any[], matchFinished: boolean): ParsedScoreboard {
  const legs: ScoreboardLeg[] = [];
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return { legs, currentLeg: null };
  }

  let prevMain: { home: number; away: number } | null = null;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const hasNextBlock = i < blocks.length - 1;
    const isFinished = hasNextBlock || matchFinished;
    const main = parseMainScore(block);
    const starterRaw = toInt(block?.SERVICE_PARTICIPANT_TYPE);
    const starter: 1 | 2 | null = starterRaw === 1 || starterRaw === 2 ? starterRaw : null;

    // --- Aufnahmen aus SCORE_HISTORY ---
    const visits: ScoreboardVisit[] = [];
    let homeRemaining = 501;
    let awayRemaining = 501;
    const history = Array.isArray(block?.SCORE_HISTORY) ? block.SCORE_HISTORY : [];

    for (const entry of history) {
      if (entry?.TYPE === 'SCORE') {
        const items = Array.isArray(entry.ITEMS) ? entry.ITEMS : [];
        for (const item of items) {
          if (toInt(item?.IS_CHANGED) !== 1) continue;
          const order = toInt(item?.TEAM_ORDER);
          const remaining = toInt(item?.VALUE);
          if ((order !== 1 && order !== 2) || remaining === null) continue;

          const prevRemaining = order === 1 ? homeRemaining : awayRemaining;
          const diff = prevRemaining - remaining;
          // Plausible Aufnahme: 0-180 Punkte. Springt der Rest nach OBEN,
          // hat innerhalb des Blocks ein neues Leg begonnen (Live-Edge-Case).
          const thrown = diff >= 0 && diff <= 180 ? diff : null;

          visits.push({ team: order as 1 | 2, thrown, remaining, highlight: null });
          if (order === 1) homeRemaining = remaining;
          else awayRemaining = remaining;
        }
      } else if (entry?.VALUE != null && visits.length > 0) {
        // TEXT_SPEC (z.B. "140", "180") gehört zur direkt vorherigen Aufnahme
        visits[visits.length - 1].highlight = String(entry.VALUE);
      }
    }

    // --- Gewinner (nur für fertige Legs) ---
    let winner: 1 | 2 | null = null;
    if (isFinished) {
      if (prevMain) {
        // Robust: Delta des Match-Stands zum Vorgänger-Block
        if (main.home > prevMain.home) winner = 1;
        else if (main.away > prevMain.away) winner = 2;
      } else if (main.home + main.away === 1) {
        // Erster Block = Leg 1: der Stand verrät den Gewinner direkt
        winner = main.home === 1 ? 1 : 2;
      }
      // Fallback: IS_CHANGED (bei eingefrorenen Blöcken korrekt)
      if (winner === null) winner = main.changedTeam;
    }

    // --- Checkout: letzter Rest des Gewinners (Checkout-Wurf fehlt in der History) ---
    let checkout: number | null = null;
    if (isFinished && winner !== null) {
      const rest = winner === 1 ? homeRemaining : awayRemaining;
      checkout = rest > 0 && rest <= 170 ? rest : null;
    }

    // --- Leg-Nummer: fertig = Summe des Stands NACH dem Leg; laufend = Summe + 1 ---
    const legNumber = isFinished ? main.home + main.away : main.home + main.away + 1;

    legs.push({
      legNumber: legNumber > 0 ? legNumber : i + 1,
      isFinished,
      winner,
      checkout,
      starter,
      visits,
      homeRemaining,
      awayRemaining,
      matchScore: isFinished ? { home: main.home, away: main.away } : null,
    });

    prevMain = { home: main.home, away: main.away };
  }

  const last = legs[legs.length - 1];
  return { legs, currentLeg: last && !last.isFinished ? last : null };
}
